import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useState, useCallback, useRef, useEffect, createContext, useContext, useMemo } from 'react';
import { usePortalContext, usePortals, RemirrorPortals } from '@remirror/extension-react-component';
import { i18n } from '@remirror/i18n';
import { addKeyToElement, RemirrorType } from '@remirror/react-utils';
import { createThemeVariables, themeStyles } from '@remirror/theme';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import isEqual from 'fast-deep-equal/react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import { object, isRemirrorManager, RemirrorManager, getLazyArray, invariant, ErrorConstant, isPlainObject, isFunction, shouldUseDomEnvironment, Cast, getDocument, Framework, isArray, isNullOrUndefined, bool } from '@remirror/core';
import { CorePreset } from '@remirror/preset-core';
import { ReactPreset } from '@remirror/preset-react';
import { useFirstMountState } from 'react-use/lib/useFirstMountState';
import useLayoutEffect from 'use-isomorphic-layout-effect';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _classPrivateFieldSet from '@babel/runtime/helpers/esm/classPrivateFieldSet';
import _classPrivateFieldGet from '@babel/runtime/helpers/esm/classPrivateFieldGet';
import composeRefs from '@seznam/compose-react-refs';
import { EditorView } from '@remirror/pm/view';
import _extends from '@babel/runtime/helpers/esm/extends';
import '@babel/runtime/helpers/createForOfIteratorHelper';
import { m as mapProps, R as ReactSerializer } from './renderer-619f24d6.browser.esm.js';
export { R as ReactSerializer, a as RenderTree, g as gatherDomMethods, m as mapProps } from './renderer-619f24d6.browser.esm.js';

/**
 * Returning a new object reference guarantees that a before-and-after
 * equivalence check will always be false, resulting in a re-render, even when
 * multiple calls to forceUpdate are batched.
 */

function useForceUpdate() {
  var _useState = useState(object()),
      _useState2 = _slicedToArray(_useState, 2),
      setState = _useState2[1];

  return useCallback(() => {
    setState(object());
  }, []);
}
/**
 * Preserves the previous version of a provided value.
 *
 * ```tsx
 * const [isOpen, setOpen] = useState<boolean>(false)
 * const previous = usePrevious(isOpen)
 *
 * return <span onClick={() => setOpen(!isOpen)}>{isOpen && previous === isOpen ? 'Stable' : 'Unstable' }</span>
 * ```
 */

function usePrevious(value) {
  var ref = useRef();
  useEffect(() => void (ref.current = value), [value]);
  return ref.current;
}
/**
 * A `useEffect` function which issues a warning when the dependencies provided
 * are deeply equal, but only in development.
 *
 * This is used in places where it's important for developers to memoize and
 * wrap methods with `useCallback`.
 */

var useEffectWithWarning = process.env.NODE_ENV === 'production' ? useEffect : (effect, deps) => {
  var ref = useRef();
  var unnecessaryChange = useRef(0);
  useEffectOnce(() => {
    ref.current = deps;
  });
  useUpdateEffect(() => {
    if (!isEqual(deps, ref.current)) {
      unnecessaryChange.current = 0;
      ref.current = deps;
      return;
    }

    unnecessaryChange.current += 1;
  });

  var wrappedEffect = () => {
    if (unnecessaryChange.current >= 1) {
      console.warn("The dependencies passed into your useEffect are deeply equal, but an update has been triggered ".concat(unnecessaryChange.current, " time(s). Please consider wrapping the values with `useMemo` or `useCallback` to memoize your dependencies and prevent unnecessary re-renders."), deps);
    }

    return effect();
  }; // eslint-disable-next-line react-hooks/exhaustive-deps


  useEffect(wrappedEffect, deps);
};

/**
 * Creates a ReactContext for the Remirror editor
 */
var RemirrorContext = /*#__PURE__*/createContext(null);
/**
 * Create the context for the i18n framework used within remirror.
 */

var I18nContext = /*#__PURE__*/createContext({
  i18n,
  locale: 'en'
});

/**
 * Create a react manager with all the default react presets and extensions.
 */
function createReactManager(combined) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  var core = options.core,
      react = options.react,
      settings = _objectWithoutProperties(options, ["core", "react"]);

  if (isRemirrorManager(combined)) {
    return combined;
  }

  return RemirrorManager.create(() => [...getLazyArray(combined), new ReactPreset(react), new CorePreset(core)], settings);
}

/**
 * This provides access to the Remirror context using hooks.
 *
 * The first argument which is optional can also be a change handler which is
 * called every time the state updates.
 *
 * @remarks
 *
 * The following example applies the root props to the div.
 * ```ts
 * import { RemirrorProvider, useRemirror } from 'remirror';
 *
 * const HooksComponent = (props) => {
 *   // This pulls the remirror props out from the context.
 *   const { getRootProps } = useRemirror();
 *
 *   return <div {...getRootProps()} />;
 * }
 *
 * class App extends Component {
 *   render() {
 *     return (
 *       <RemirrorProvider>
 *         <HooksComponent />
 *       </RemirrorProvider>
 *     );
 *   }
 * }
 * ```
 *
 * For performance reasons `useRemirror` does not automatically trigger a
 * rerender on every editor update. This allows for you use it in component
 * which don't need to track the latest editor state, without suffering a
 * performance penalty.
 *
 * However if you do want to track whether a command is enabled at the current
 * selection or whether a certain formatting mark (bold) is active at the
 * current selection you can pass through an optional parameter.
 *
 * ```
 * const { active, commands } = useRemirror({ autoUpdate: true });
 *
 * return (
 *   <button style={{ fontWeight: active.bold() ? 'bold' : undefined }}>
 *     B
 *   <button>
 * )
 * ```
 *
 * The above example keep track of whether the current selection is bold on
 * every update to the editor. Updates can be document changes and selection
 * changes.
 *
 * For more control you can also use a handler function as the first parameter
 * to selectively rerender as you see fit.
 *
 * ```
 * const { active, commands } = useRemirror(() => {
 *   if (active.bold() === boldActive) {
 *     return;
 *   }
 *
 *   setBoldActive(active.bold());
 * });
 *
 * const [boldActive, setBoldActive] = useState(active.bold());
 *
 * return (
 *   <button style={{ fontWeight: boldActive ? 'bold' : undefined }}>
 *     B
 *   <button>
 * )
 * ```
 *
 * In this case the component only re-renders when the bold formatting is no
 * longer active.
 */

function useRemirror(handler) {
  // This is not null when rendering within the `RemirrorProvider`. The majority
  // of times this is called, this will be the case.
  var editorContext = useContext(RemirrorContext); // This is not null when rendering within the `PortalContext` for custom node
  // views and custom decorations.

  var portalContext = usePortalContext(); // A helper for forcing an update of the state.

  var forceUpdate = useRef(useForceUpdate()); // Pick the correct context to use.

  var context = editorContext !== null && editorContext !== void 0 ? editorContext : portalContext; // Throw an error if context doesn't exist.

  !context ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.REACT_PROVIDER_CONTEXT
  }) : invariant(false) : void 0; // Destructure the `addHandler` to minimise updates in the `useEffect` hook.

  var addHandler = context.addHandler; // Manages the update frequency of this hook.

  useEffect(() => {
    var updateHandler = handler; // Default state, do nothing.

    if (!updateHandler) {
      return;
    } // Use the `forceUpdate` handler when `autoUpdate` is true.


    if (isPlainObject(updateHandler)) {
      var _updateHandler = updateHandler,
          autoUpdate = _updateHandler.autoUpdate;
      updateHandler = autoUpdate ? () => forceUpdate.current() : undefined;
    }

    if (!isFunction(updateHandler)) {
      return;
    } // Add the update handler which will update this hook every time the editor
    // is updated.


    return addHandler('updated', updateHandler);
  }, [addHandler, handler]);
  return context;
}
function useI18n() {
  var context = useContext(I18nContext); // Throw an error if no context exists.

  !context ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.I18N_CONTEXT
  }) : invariant(false) : void 0;
  return context;
}
/**
 * This is a type alias for creating your own typed version of the remirror
 * method.
 *
 * ```ts
 * import { useRemirror, UseRemirrorType } from 'remirror/react';
 * import { SocialPreset } from 'remirror/preset/social'
 *
 * const useSocialRemirror = useRemirror as UseRemirrorType<SocialPreset>;
 *
 * // With the remirror provider context.
 * const Editor = () => {
 *   const { commands } = useSocialRemirror();
 *
 *   // All available commands are shown with intellisense. Command click to goto the implementation.
 *   commands.toggleBold();
 * }
 * ```
 */

function useExtension(Constructor) {
  var optionsOrCallback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : undefined;
  var dependencies = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];

  var _useRemirror = useRemirror(),
      getExtension = _useRemirror.getExtension;

  var extension = useMemo(() => getExtension(Constructor), [Constructor, getExtension]); // Handle the case where an options object is passed in.

  useEffectWithWarning(() => {
    if (isFunction(optionsOrCallback) || !optionsOrCallback) {
      return;
    }

    extension.setOptions(optionsOrCallback);
  }, [extension, optionsOrCallback]);
  useEffectWithWarning(() => {
    if (!isFunction(optionsOrCallback)) {
      return;
    }

    return optionsOrCallback({
      addHandler: extension.addHandler.bind(extension),
      addCustomHandler: extension.addCustomHandler.bind(extension),
      extension
    });
  }, [extension, optionsOrCallback, ...dependencies]); // Return early when no extension available.

  if (optionsOrCallback) {
    return;
  }

  return extension;
}
function usePreset(Constructor) {
  var optionsOrCallback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : undefined;
  var dependencies = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];

  var _useRemirror2 = useRemirror(),
      getPreset = _useRemirror2.getPreset;

  var preset = useMemo(() => getPreset(Constructor), [Constructor, getPreset]);
  useEffectWithWarning(() => {
    if (isFunction(optionsOrCallback) || !optionsOrCallback) {
      return;
    }

    preset.setOptions(optionsOrCallback);
  }, [preset, optionsOrCallback]);
  useEffectWithWarning(() => {
    if (!isFunction(optionsOrCallback)) {
      return;
    }

    return optionsOrCallback({
      addHandler: preset.addHandler.bind(preset),
      addCustomHandler: preset.addCustomHandler.bind(preset),
      preset
    });
  }, [preset, optionsOrCallback, ...dependencies]); // Return early when the options or callback parameter is provided.

  if (optionsOrCallback) {
    return;
  }

  return preset;
}

/**
 * A hook for creating the editor manager directly in the react component.
 *
 * @remarks
 *
 * The manager is a singleton and doesn't rerender until `manager.destroy()` is
 * called. You should call this method in a `useEffect`
 *
 * This is intentional. However, it's something that can be addressed if it
 * causes issues.
 *
 * ```tsx
 * import { useExtension } from 'remirror/react';
 * import { PresetCore } from 'remirror/preset-core';
 * import { BoldExtension } from 'remirror/extension-bold';
 *
 * const Framework = () => {
 *   const manager = useManager([new BoldExtension(), new CorePreset()]);
 *
 *   <RemirrorProvider >
 *     <MyEditor />
 *   </RemirrorProvider>;
 * }
 * ```
 */
function useManager(combined) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  // Store the value in refs so that they can be used in the `useEffect` hook
  // without being passed as dependencies.
  var combinedRef = useRef(combined);
  var optionsRef = useRef(options);

  var _useState = useState(() => createReactManager(combined, options)),
      _useState2 = _slicedToArray(_useState, 2),
      manager = _useState2[0],
      setManager = _useState2[1]; // Keep the parameter refs up to date with the latest value.


  combinedRef.current = combined;
  optionsRef.current = options; // Add a manager destroyed listener to only cleanup the manager when it is
  // destroyed.

  useEffect(() => {
    return manager.addHandler('destroy', () => {
      // `clone` is used to ensure that that any stale extensions are
      // reinitialized.
      setManager(() => createReactManager(combinedRef.current, optionsRef.current).clone());
    });
  }, [manager]);
  return manager;
}

/**
 * A mock editor view used only when prosemirror is running on the server
 */

class EditorViewSSR {
  constructor(_place, props) {
    _defineProperty(this, "dragging", null);

    var doc = getDocument('ssr');
    this.root = doc;
    this.dom = doc.createElement('div');
    this.state = props.state;
  }

  update(_props) {}

  setProps(_props) {}

  updateState(_state) {}

  someProp(_propName, f) {
    return f ? f(null) : null;
  }

  hasFocus() {
    return false;
  }

  focus() {}

  posAtCoords(_coords) {
    return null;
  }

  coordsAtPos(_pos) {
    return {
      bottom: 0,
      left: 0,
      right: 0,
      top: 0
    };
  }

  domAtPos(_pos) {
    return {
      node: this.dom,
      offset: 0
    };
  }

  nodeDOM(_pos) {
    return null;
  }

  posAtDOM(_node, _offset, _bias) {
    return 0;
  }

  endOfTextblock(_dir, _state) {
    return true;
  }
  /**
   * Removes the editor from the DOM and destroys all [node
   * views](#view.NodeView).
   */


  destroy() {}

  dispatch(_tr) {}

}
/**
 * Creates a new editor view
 *
 * @param place
 * @param props
 * @param forceEnvironment
 */

var createEditorView = (place, props, forceEnvironment) => {
  var Constructor = shouldUseDomEnvironment(forceEnvironment) ? EditorView : Cast(EditorViewSSR);
  return new Constructor(place, props);
};

/**
 * Remirror SSR component used for rendering in non dom environments.
 */
var RemirrorSSR = props => {
  var attributes = props.attributes,
      manager = props.manager,
      state = props.state,
      editable = props.editable;
  var outerProperties = mapProps(attributes);
  var ssrElement = ReactSerializer.fromManager(manager).serializeFragment(state.doc.content);
  var transformedElement = manager.store.ssrTransformer(ssrElement, state);
  return /*#__PURE__*/React.createElement("div", _extends({}, outerProperties, {
    suppressContentEditableWarning: true,
    contentEditable: editable
  }), transformedElement);
};

var _getShouldRenderClient = /*#__PURE__*/new WeakMap();

var _setShouldRenderClient = /*#__PURE__*/new WeakMap();

var _editorRef = /*#__PURE__*/new WeakMap();

class ReactFramework extends Framework {
  /**
   * Whether to render the client immediately.
   */

  /**
   * Update the should render client state input.
   */

  /**
   * Stores the Prosemirror EditorView dom element.
   */

  /**
   * Used when suppressHydrationWarning is true to determine when it's okay to
   * render the client content.
   */
  get shouldRenderClient() {
    return _classPrivateFieldGet(this, _getShouldRenderClient).call(this);
  }
  /**
   * Keep track of whether the get root props has been called during the most recent render.
   */


  get name() {
    return 'react';
  }

  constructor(parameter) {
    var _this$props$placehold;

    super(parameter);

    _getShouldRenderClient.set(this, {
      writable: true,
      value: void 0
    });

    _setShouldRenderClient.set(this, {
      writable: true,
      value: void 0
    });

    _editorRef.set(this, {
      writable: true,
      value: void 0
    });

    _defineProperty(this, "rootPropsConfig", {
      called: false,
      count: 0
    });

    _defineProperty(this, "getRootProps", options => {
      return this.internalGetRootProps(options, null);
    });

    _defineProperty(this, "internalGetRootProps", (options, children) => {
      // Ensure that this is the first time `getRootProps` is being called during
      // this commit phase of the .
      // invariant(!this.rootPropsConfig.called, { code: ErrorConstant.REACT_GET_ROOT_PROPS });
      this.rootPropsConfig.called = true;

      var _ref = options !== null && options !== void 0 ? options : object(),
          _ref$refKey = _ref.refKey,
          refKey = _ref$refKey === void 0 ? 'ref' : _ref$refKey,
          ref = _ref.ref,
          config = _objectWithoutProperties(_ref, ["refKey", "ref"]);

      return _objectSpread(_objectSpread({
        [refKey]: composeRefs(ref, this.onRef),
        key: this.uid
      }, config), {}, {
        children: children !== null && children !== void 0 ? children : this.renderChildren(null)
      });
    });

    _defineProperty(this, "onRef", element => {
      if (!element) {
        return;
      }

      this.rootPropsConfig.count += 1;
      !(this.rootPropsConfig.count <= 1) ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.REACT_GET_ROOT_PROPS,
        message: "Called ".concat(this.rootPropsConfig.count, " times")
      }) : invariant(false) : void 0;

      _classPrivateFieldSet(this, _editorRef, element);

      this.onRefLoad();
    });

    var getShouldRenderClient = parameter.getShouldRenderClient,
        setShouldRenderClient = parameter.setShouldRenderClient;

    _classPrivateFieldSet(this, _getShouldRenderClient, getShouldRenderClient);

    _classPrivateFieldSet(this, _setShouldRenderClient, setShouldRenderClient);

    if (this.manager.view) {
      this.manager.view.setProps({
        state: this.manager.view.state,
        dispatchTransaction: this.dispatchTransaction,
        attributes: () => this.getAttributes(),
        editable: () => {
          var _this$props$editable;

          return (_this$props$editable = this.props.editable) !== null && _this$props$editable !== void 0 ? _this$props$editable : true;
        }
      });
      return;
    }

    this.manager.getPreset(ReactPreset).setOptions({
      placeholder: (_this$props$placehold = this.props.placeholder) !== null && _this$props$placehold !== void 0 ? _this$props$placehold : ''
    });
  }
  /**
   * This is called to update props on every render so that values don't become stale.
   */


  update(parameter) {
    super.update(parameter);
    var getShouldRenderClient = parameter.getShouldRenderClient,
        setShouldRenderClient = parameter.setShouldRenderClient;

    _classPrivateFieldSet(this, _getShouldRenderClient, getShouldRenderClient);

    _classPrivateFieldSet(this, _setShouldRenderClient, setShouldRenderClient);

    return this;
  }
  /**
   * Create the prosemirror editor view.
   */


  createView(state) {
    return createEditorView(undefined, {
      state,
      nodeViews: this.manager.store.nodeViews,
      dispatchTransaction: this.dispatchTransaction,
      attributes: () => this.getAttributes(),
      editable: () => {
        var _this$props$editable2;

        return (_this$props$editable2 = this.props.editable) !== null && _this$props$editable2 !== void 0 ? _this$props$editable2 : true;
      }
    }, this.props.forceEnvironment);
  }
  /**
   * The external `getRootProps` that is used to spread props onto a desired
   * holder element for the prosemirror view.
   */


  /**
   * Updates the state either by calling onStateChange when it exists or
   * directly setting the internal state via a `setState` call.
   */
  updateState(parameter) {
    var state = parameter.state,
        _parameter$triggerCha = parameter.triggerChange,
        triggerChange = _parameter$triggerCha === void 0 ? true : _parameter$triggerCha,
        tr = parameter.tr,
        transactions = parameter.transactions;

    if (this.props.value) {
      var onChange = this.props.onChange;
      !onChange ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.REACT_CONTROLLED,
        message: 'You are required to provide the `onChange` handler when creating a controlled editor.'
      }) : invariant(false) : void 0;
      !triggerChange ? process.env.NODE_ENV !== "production" ? invariant(false, {
        code: ErrorConstant.REACT_CONTROLLED,
        message: 'Controlled editors do not support `clearContent` or `setContent` where `triggerChange` is `true`. Update the `value` prop instead.'
      }) : invariant(false) : void 0;

      if (!this.previousStateOverride) {
        this.previousStateOverride = this.getState();
      }

      this.onChange({
        state,
        tr
      });
      return;
    } // Update the internal prosemirror state. This happens before we update
    // the component's copy of the state.


    this.view.updateState(state);

    if (triggerChange) {
      // Update the `onChange` handler before notifying the manager but only when a change should be triggered.
      this.onChange({
        state,
        tr
      });
    }

    this.manager.onStateUpdate({
      previousState: this.previousState,
      state,
      tr,
      transactions
    });
  }
  /**
   * Update the controlled state when the value changes and notify the extension
   * of this update.
   */


  updateControlledState(state, previousState) {
    this.previousStateOverride = previousState;
    this.view.updateState(state);
    this.manager.onStateUpdate({
      previousState: this.previousState,
      state
    });
    this.previousStateOverride = undefined;
  }
  /**
   * Adds the prosemirror view to the dom in the position specified via the
   * component props.
   */


  addProsemirrorViewToDom(element, viewDom) {
    if (this.props.insertPosition === 'start') {
      element.insertBefore(viewDom, element.firstChild);
    } else {
      element.append(viewDom);
    }
  }
  /**
   * Called once the container dom node (`this.editorRef`) has been initialized
   * after the component mounts.
   *
   * This method handles the cases where the dom is not focused.
   */


  onRefLoad() {
    !_classPrivateFieldGet(this, _editorRef) ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.REACT_EDITOR_VIEW,
      message: 'Something went wrong when initializing the text editor. Please check your setup.'
    }) : invariant(false) : void 0;
    var autoFocus = this.props.autoFocus;
    this.addProsemirrorViewToDom(_classPrivateFieldGet(this, _editorRef), this.view.dom);

    if (autoFocus) {
      this.focus(autoFocus);
    }

    this.onChange();
    this.addFocusListeners();
  }

  onMount() {
    var suppressHydrationWarning = this.props.suppressHydrationWarning;

    if (suppressHydrationWarning) {
      _classPrivateFieldGet(this, _setShouldRenderClient).call(this, true);
    }
  }
  /**
   * Called for every update of the props and state.
   */


  onUpdate(previousEditable) {
    // Ensure that `children` is still a render prop
    // propIsFunction(this.props.children);
    // Check whether the editable prop has been updated
    if (this.props.editable !== previousEditable && this.view && _classPrivateFieldGet(this, _editorRef)) {
      this.view.setProps(_objectSpread(_objectSpread({}, this.view.props), {}, {
        editable: () => {
          var _this$props$editable3;

          return (_this$props$editable3 = this.props.editable) !== null && _this$props$editable3 !== void 0 ? _this$props$editable3 : true;
        }
      }));
    }
  }

  get frameworkOutput() {
    return _objectSpread(_objectSpread({}, this.baseOutput), {}, {
      getRootProps: this.getRootProps,
      portalContainer: this.manager.store.portalContainer
    });
  }
  /**
   * Checks whether this is an SSR environment and returns a child array with
   * the SSR component
   *
   * @param children
   */


  renderChildren(child) {
    var _this$props = this.props,
        forceEnvironment = _this$props.forceEnvironment,
        _this$props$insertPos = _this$props.insertPosition,
        insertPosition = _this$props$insertPos === void 0 ? 'end' : _this$props$insertPos,
        suppressHydrationWarning = _this$props.suppressHydrationWarning;
    var children = isArray(child) ? child : [child];

    if (shouldUseDomEnvironment(forceEnvironment) && (!suppressHydrationWarning || this.shouldRenderClient)) {
      return children;
    }

    var ssrElement = this.renderSSR();
    return (insertPosition === 'start' ? [ssrElement, ...children] : [...children, ssrElement]).map(addKeyToElement);
  }
  /**
   * Return a JSX Element to be used within the domless environment.
   */


  renderSSR() {
    var _this$props$editable4;

    return /*#__PURE__*/React.createElement(RemirrorSSR, {
      attributes: this.getAttributes(true),
      state: this.getState(),
      manager: this.manager,
      editable: (_this$props$editable4 = this.props.editable) !== null && _this$props$editable4 !== void 0 ? _this$props$editable4 : true
    });
  }
  /**
   * Reset the called status of `getRootProps`.
   */


  resetRender() {
    // Reset the status of roots props being called
    this.rootPropsConfig.called = false;
    this.rootPropsConfig.count = 0;
  }

}

/**
 * The hook responsible for providing the editor context to the `RemirrorProvider`.
 *
 * @remarks
 *
 * This is an internal component and should only be used within the `remirror`
 * codebase. The `RemirrorProvider` is the only supported way for consuming the
 * application.
 *
 * @internal
 */

function useReactEditor(props) {
  var _props$initialContent;

  var _props$stringHandler = props.stringHandler,
      stringHandler = _props$stringHandler === void 0 ? defaultStringHandler : _props$stringHandler,
      onError = props.onError,
      manager = props.manager,
      forceEnvironment = props.forceEnvironment,
      value = props.value;
  var placeholder = props.placeholder;
  var isFirstMount = useFirstMountState(); // Update the placeholder on first render.

  if (isFirstMount && !isNullOrUndefined(placeholder)) {
    manager.getPreset(ReactPreset).setOptions({
      placeholder
    });
  } // Keep the placeholder updated


  useUpdateEffect(() => {
    if (isNullOrUndefined(placeholder)) {
      return;
    }

    manager.getPreset(ReactPreset).setOptions({
      placeholder
    });
  }, [placeholder, manager]);
  var createStateFromContent = useCallback((content, selection) => {
    return manager.createState({
      content,
      document: getDocument(forceEnvironment),
      stringHandler,
      selection,
      onError
    });
  }, [onError, forceEnvironment, manager, stringHandler]);
  var fallback = manager.createEmptyDoc();

  var _ref = isArray(props.initialContent) ? props.initialContent : [(_props$initialContent = props.initialContent) !== null && _props$initialContent !== void 0 ? _props$initialContent : fallback],
      _ref2 = _slicedToArray(_ref, 2),
      initialContent = _ref2[0],
      initialSelection = _ref2[1];

  var initialEditorState = bool(value) ? value : createStateFromContent(initialContent, initialSelection);

  var _useState = useState(props.suppressHydrationWarning ? false : undefined),
      _useState2 = _slicedToArray(_useState, 2),
      shouldRenderClient = _useState2[0],
      setShouldRenderClient = _useState2[1]; // Store all the `logic` in a `ref`


  var framework = useFramework({
    initialEditorState,
    setShouldRenderClient,
    createStateFromContent,
    getProps: () => props,
    getShouldRenderClient: () => shouldRenderClient
  });
  useEffect(() => {
    framework.onMount();
    return () => {
      framework.destroy();
    };
  }, [framework]);
  var previousEditable = usePrevious(props.editable); // Handle editor updates

  useEffect(() => {
    framework.onUpdate(previousEditable);
  }, [previousEditable, framework]); // Handle the controlled editor usage.

  useControlledEditor(framework);
  return framework.frameworkOutput;
}
/**
 * A hook which creates a reference to the `ReactFramework` and updates the
 * parameters on every render.
 */

function useFramework(parameter) {
  var parameterRef = useRef(parameter);
  parameterRef.current = parameter;

  var _useState3 = useState(() => new ReactFramework(parameterRef.current)),
      _useState4 = _slicedToArray(_useState3, 2),
      framework = _useState4[0],
      setFramework = _useState4[1];

  framework.update(parameter);
  useEffect(() => {
    framework.props.manager.addHandler('destroy', () => {
      setFramework(() => new ReactFramework(parameterRef.current));
    });
  }, [framework.props.manager]);
  return framework;
}
/**
 * If no string handler is provided, but the user tries to provide a string as
 * content then throw an error.
 */


function defaultStringHandler() {
   process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.REACT_EDITOR_VIEW,
    message: 'No valid string handler. In order to pass in `string` as `initialContent` to the remirror editor you must provide a valid stringHandler prop'
  }) : invariant(false) ;
}
/**
 * A hook which manages the controlled updates for the editor.
 */


function useControlledEditor(framework) {
  var value = framework.props.value; // Cache whether this is a controlled editor.

  var isControlledRef = useRef(bool(value));
  var previousValue = usePrevious(value); // Using layout effect for synchronous updates.

  useLayoutEffect(() => {
    // Check if the update is valid based on current value.
    var validUpdate = value ? isControlledRef.current === true : isControlledRef.current === false;
    !validUpdate ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.REACT_CONTROLLED,
      message: isControlledRef.current ? 'You have attempted to switch from a controlled to an uncontrolled editor. Once you set up an editor as a controlled editor it must always provide a `value` prop.' : 'You have provided a `value` prop to an uncontrolled editor. In order to set up your editor as controlled you must provide the `value` prop from the very first render.'
    }) : invariant(false) : void 0;

    if (!value || value === previousValue) {
      return;
    }

    framework.updateControlledState(value, previousValue !== null && previousValue !== void 0 ? previousValue : undefined);
  }, [value, previousValue, framework]);
}

/**
 * A default internal editor for the react framework.
 */
var AutoRenderedEditor = () => /*#__PURE__*/React.createElement("div", useRemirror().getRootProps());
/**
 * The RemirrorProvider which injects context into it's child component.
 *
 * @remarks
 * This only supports one child. At the moment if that that child is a built in
 * html string element then it is also where the prosemirror editor will be
 * injected (root element).
 *
 * These can either be consumed using React Hooks
 * - `useRemirror`
 * - `usePositioner`
 */


var RemirrorProvider = props => {
  var _ref;

  var children = props.children,
      autoRender = props.autoRender,
      manager = props.manager,
      combined = props.combined,
      settings = props.settings,
      rest = _objectWithoutProperties(props, ["children", "autoRender", "manager", "combined", "settings"]);

  var context = useReactEditor(_objectSpread(_objectSpread({}, rest), {}, {
    manager: useManager((_ref = manager !== null && manager !== void 0 ? manager : combined) !== null && _ref !== void 0 ? _ref : [], settings)
  })); // Subscribe to updates from the [[`PortalContainer`]]

  var portals = usePortals(context.portalContainer);
  return /*#__PURE__*/React.createElement(RemirrorContext.Provider, {
    value: context
  }, /*#__PURE__*/React.createElement(RemirrorPortals, {
    portals: portals,
    context: context
  }), (autoRender === 'start' || autoRender === true) && /*#__PURE__*/React.createElement(AutoRenderedEditor, null), children, autoRender === 'end' && /*#__PURE__*/React.createElement(AutoRenderedEditor, null));
};
RemirrorProvider.$$remirrorType = RemirrorType.Provider;

/**
 * A provider component for the remirror i18n helper library.
 *
 * This uses `@lingui/core` in the background. So please star and support the
 * project when you have a moment.
 */
var I18nProvider = props => {
  var _props$i18n = props.i18n,
      i18n$1 = _props$i18n === void 0 ? i18n : _props$i18n,
      _props$locale = props.locale,
      locale = _props$locale === void 0 ? 'en' : _props$locale,
      supportedLocales = props.supportedLocales,
      children = props.children;
  useEffect(() => {
    i18n$1.activate(locale, supportedLocales !== null && supportedLocales !== void 0 ? supportedLocales : [locale]);
  }, [i18n$1, locale, supportedLocales]);
  return /*#__PURE__*/React.createElement(I18nContext.Provider, {
    value: {
      i18n: i18n$1,
      locale,
      supportedLocales
    }
  }, children);
};
I18nProvider.$$remirrorType = RemirrorType.I18nProvider;

/**
 * This the `ThemeProvider`. Wrap your editor with it to customise the theming
 * of content within your editor.
 *
 * Please be aware that this wraps your component in an extra dom layer.
 */
var ThemeProvider = props => {
  var theme = props.theme,
      children = props.children,
      _props$as = props.as,
      Component = _props$as === void 0 ? 'div' : _props$as;
  return /*#__PURE__*/React.createElement(Component, {
    style: createThemeVariables(theme).styles,
    className: themeStyles
  }, children);
};
ThemeProvider.$$remirrorType = RemirrorType.ThemeProvider;

export { I18nProvider, RemirrorProvider, ThemeProvider, createEditorView, createReactManager, useEffectWithWarning, useExtension, useForceUpdate, useI18n, useManager, usePreset, usePrevious, useRemirror };
